import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { listProjects, createProject, deleteProject, saveReportMeta, seedDemoProject, listSeedCatalogue, getWizardStepProgress, updateProject, api, getProjectCounts } from "@/lib/api";
import { commoditiesByCategory, getCommodity } from "@/lib/commodity";
import { Plus, Trash, ArrowRight, Database, CaretDown, CaretRight, Buildings, IdentificationCard, MapPin, Sparkle, Play } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
// Feb 2026 · Iter87 · Show the 9-step stage label (not the internal
// 20-item wizard step) on the Resume button so the user sees exactly
// the same numbering as the sidebar.
import {
  WORKFLOW_STAGES, resolveStageForWizardStep,
} from "@/config/simplified_flow";
import BulkCRSFixDialog from "@/pages/dashboard/BulkCRSFixDialog";
import LifecycleFunnel from "@/pages/dashboard/LifecycleFunnel";
import PortfolioHero from "@/pages/dashboard/PortfolioHero";
// Iter98k R8 · Fleet analytics moved to /app/admin/analytics — imports
// removed from this file so the Projects landing page stays lean.
import RestoreWizardDialog from "@/pages/dashboard/RestoreWizardDialog";
import { useConfirmDialog } from "@/components/ConfirmDialog";

// Grade-unit families — mapped to the canonical `gradeUnit` on the commodity.
// The user can override the display symbol at project-creation time; the
// underlying math still uses the canonical family.
const GRADE_UNIT_OPTIONS = {
  pct: ["%", "wt%"],
  g_t: ["g/t", "oz/t", "kg/t"],
  ppm: ["ppm", "%", "ppb"],
};

function Section({ title, icon: Icon, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-sm bg-white/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-widest text-brand-blue hover:bg-brand-blue/5"
      >
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        {Icon ? <Icon size={13} /> : null}
        {title}
      </button>
      {open ? <div className="px-3 pb-3 space-y-3">{children}</div> : null}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">
        {label}
      </Label>
      <Input {...props} className="mono bg-white border-slate-200 rounded-sm h-9" />
    </div>
  );
}

function CreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [commodity, setCommodity] = useState("Fe");
  const [gradeSymbol, setGradeSymbol] = useState("%");
  const [description, setDescription] = useState("");
  const [workspace, setWorkspace] = useState("intelligent");
  // Company / project / CP fields — passed through to /report-meta after
  // the project is created so they show up on every generated PDF/DOCX.
  const [meta, setMeta] = useState({
    client_name: "",
    client_contact_name: "",
    client_contact_role: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    project_location: "",
    project_country: "",
    project_state_region: "",
    deposit_type: "",
    prepared_by: "",
    prepared_membership: "",
    reviewed_by: "",
    approved_by: "",
    approved_position: "",
  });
  const setMetaField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

  const c = getCommodity(commodity);
  const unitOptions = GRADE_UNIT_OPTIONS[c.gradeUnit] || [c.gradeSymbol];

  // Sync grade-symbol default when commodity changes
  const onCommodityChange = (code) => {
    setCommodity(code);
    const nc = getCommodity(code);
    setGradeSymbol(nc.gradeSymbol);
  };

  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: async () => {
      const project = await createProject({ name, commodity, description, grade_symbol: gradeSymbol, workspace });
      // Push CP + client + project meta if the user filled anything.
      const cleanMeta = Object.fromEntries(
        Object.entries(meta).filter(([, v]) => v && String(v).trim())
      );
      if (Object.keys(cleanMeta).length) {
        try { await saveReportMeta(project.id, cleanMeta); } catch { /* non-fatal */ }
      }
      return project;
    },
    onSuccess: (p) => {
      toast.success(`Project "${p.name}" created`);
      qc.invalidateQueries({ queryKey: ["projects"] });
      setOpen(false); setName(""); setDescription("");
      setMeta({
        client_name: "", client_contact_name: "", client_contact_role: "",
        client_email: "", client_phone: "", client_address: "",
        project_location: "", project_country: "", project_state_region: "",
        deposit_type: "", prepared_by: "", prepared_membership: "",
        reviewed_by: "", approved_by: "", approved_position: "",
      });
      onCreated?.(p);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to create project"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        {/* Primary — Sprint P31 · Intelligent auto-configuration */}
        <Link to="/app/projects/new">
          <Button className="bg-brand-blue hover:bg-brand-blueDeep text-white rounded-sm font-medium"
                   data-testid="new-project-btn">
            <Sparkle size={16} weight="fill" className="mr-2" /> New Project
          </Button>
        </Link>
      </div>
      <DialogContent className="bg-surface border-slate-200 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-widest uppercase text-sm">Create Project</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Set up a new drillhole database workspace. Additional company / CP details are
            optional here — they can be edited any time from the Report Setup dialog.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="project-name-input"
                placeholder="e.g. Gold Valley Mining Project" className="mono bg-white border-slate-200 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Commodity</Label>
              <select
                value={commodity}
                onChange={(e) => onCommodityChange(e.target.value)}
                data-testid="project-commodity-input"
                className="mono bg-white border border-slate-200 rounded-sm w-full h-10 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue"
              >
                {commoditiesByCategory().map((g) => (
                  <optgroup key={g.category} label={g.label.toUpperCase()}>
                    {g.items.map((c2) => (
                      <option key={c2.code} value={c2.code}>
                        {c2.code} — {c2.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Grade Unit</Label>
              <select
                value={gradeSymbol}
                onChange={(e) => setGradeSymbol(e.target.value)}
                data-testid="project-grade-unit-input"
                className="mono bg-white border border-slate-200 rounded-sm w-full h-10 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue"
              >
                {unitOptions.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500/70 mt-1 mono">
                Cut-off, density and search radius are AI-suggested from your imported data on the Block Model tab.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)}
                data-testid="project-description-input"
                placeholder="e.g. Exploration project — Phase 2 drilling"
                className="mono bg-white border-slate-200 rounded-sm h-10" />
            </div>
          </div>

          {/* --- Workspace persona ------------------------------------- */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">
              Select project workspace
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-1"
                  data-testid="new-project-workspace">
              {[
                { key: "intelligent", label: "Intelligent Workspace",
                  color: "amber",
                  hint: "SRES analyses data, recommends parameters, generates models & alternatives, and prepares outputs. Results remain editable and require review." },
                { key: "professional", label: "Professional Workspace",
                  color: "cyan",
                  hint: "Full CAD toolbars, layer manager, coordinate entry, string / surface / wireframe editing, block-model configuration, resource-estimation, pit-design and reporting controls." },
              ].map((w) => {
                const active = workspace === w.key;
                return (
                  <button key={w.key}
                    type="button"
                    onClick={() => setWorkspace(w.key)}
                    data-testid={`new-project-workspace-${w.key}`}
                    className={`text-left rounded-md border-2 p-2 transition-all
                                  ${active
                                    ? w.color === "amber"
                                      ? "border-amber-500 bg-amber-50"
                                      : "border-cyan-500 bg-cyan-50"
                                    : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className={`text-xs font-semibold ${w.color === "amber"
                        ? "text-amber-600" : "text-cyan-600"}`}>
                      {w.label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {w.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* --- Company / Client details ------------------------------- */}
          <Section title="Company / Client Details" icon={Buildings} testid="section-company">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Client / Company Name" value={meta.client_name}
                onChange={(e) => setMetaField("client_name", e.target.value)}
                placeholder="e.g. Gold Valley Mining Ltd."
                data-testid="meta-client-name" />
              <Field label="Contact Person" value={meta.client_contact_name}
                onChange={(e) => setMetaField("client_contact_name", e.target.value)}
                placeholder="Full name" data-testid="meta-contact-name" />
              <Field label="Contact Role / Designation" value={meta.client_contact_role}
                onChange={(e) => setMetaField("client_contact_role", e.target.value)}
                placeholder="e.g. Exploration Manager" data-testid="meta-contact-role" />
              <Field label="Email" type="email" value={meta.client_email}
                onChange={(e) => setMetaField("client_email", e.target.value)}
                placeholder="contact@company.com" data-testid="meta-client-email" />
              <Field label="Phone" value={meta.client_phone}
                onChange={(e) => setMetaField("client_phone", e.target.value)}
                placeholder="+91-XXXX-XXX-XXX" data-testid="meta-client-phone" />
              <Field label="Address" value={meta.client_address}
                onChange={(e) => setMetaField("client_address", e.target.value)}
                placeholder="City, State" data-testid="meta-client-address" />
            </div>
          </Section>

          {/* --- Project details --------------------------------------- */}
          <Section title="Project Details" icon={MapPin} testid="section-project">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Location / Region" value={meta.project_location}
                onChange={(e) => setMetaField("project_location", e.target.value)}
                placeholder="e.g. Bellary Iron Ore Belt" data-testid="meta-project-location" />
              <Field label="Country" value={meta.project_country}
                onChange={(e) => setMetaField("project_country", e.target.value)}
                placeholder="India" data-testid="meta-project-country" />
              <Field label="State / Region" value={meta.project_state_region}
                onChange={(e) => setMetaField("project_state_region", e.target.value)}
                placeholder="Karnataka" data-testid="meta-project-state" />
              <Field label="Deposit Type" value={meta.deposit_type}
                onChange={(e) => setMetaField("deposit_type", e.target.value)}
                placeholder="e.g. BIF-hosted iron ore" data-testid="meta-deposit-type" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                Long Description / Scope of Work
              </Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                data-testid="project-long-description"
                placeholder="Detailed project scope, background, mineralisation style…"
                className="bg-white border-slate-200 rounded-sm text-xs" />
            </div>
          </Section>

          {/* --- Competent Person -------------------------------------- */}
          <Section title="Competent Person / QP Details" icon={IdentificationCard} testid="section-cp">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Prepared By (CP Name)" value={meta.prepared_by}
                onChange={(e) => setMetaField("prepared_by", e.target.value)}
                placeholder="e.g. A. K. Sharma"
                data-testid="meta-prepared-by" />
              <Field label="Membership / Registration" value={meta.prepared_membership}
                onChange={(e) => setMetaField("prepared_membership", e.target.value)}
                placeholder="e.g. FAusIMM (CP), P.Geo"
                data-testid="meta-prepared-membership" />
              <Field label="Reviewed By" value={meta.reviewed_by}
                onChange={(e) => setMetaField("reviewed_by", e.target.value)}
                placeholder="Reviewer full name" data-testid="meta-reviewed-by" />
              <Field label="Approved By" value={meta.approved_by}
                onChange={(e) => setMetaField("approved_by", e.target.value)}
                placeholder="e.g. Managing Director" data-testid="meta-approved-by" />
              <Field label="Approver Position / Designation" value={meta.approved_position}
                onChange={(e) => setMetaField("approved_position", e.target.value)}
                placeholder="e.g. Project Sponsor" data-testid="meta-approved-position" />
            </div>
            <div className="text-[10px] text-slate-500/70 mono">
              You can upload signature images later from the Report Setup dialog.
            </div>
          </Section>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="cancel-create-btn">Cancel</Button>
          <Button
            onClick={() => name.trim() && mut.mutate()}
            disabled={!name.trim() || mut.isPending}
            className="bg-brand-blue hover:bg-brand-blueDeep text-white rounded-sm"
            data-testid="submit-create-btn"
          >
            {mut.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  // Iter89 · Feb 2026 · View-Archived toggle. When true, GET /api/projects
  // is called with ?include_archived=true so archived cards appear in the
  // grid (visually dimmed + Restore action available).
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", { includeArchived }],
    queryFn: () => listProjects({ includeArchived }),
  });
  // Iter90 · replace the always-on full-list side-query with the
  // lightweight counts endpoint. 3 numbers instead of a full array.
  const { data: counts } = useQuery({
    queryKey: ["projects", "counts"],
    queryFn: getProjectCounts,
    staleTime: 60_000,
  });
  const archivedTotal = counts?.archived ?? 0;
  const del = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project deleted"); },
  });

  return (
    <div className="h-full overflow-auto animate-fade-in" data-testid="projects-page">
      {/* Premium Portfolio Hero (Phase 1 · Feb 2026) — zero-gap gold/navy
          hero panel that flows edge-to-edge on all breakpoints. */}
      <PortfolioHero projects={projects} counts={counts} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-heading font-black tracking-tight text-slate-900">
              Projects
            </h2>
            <p className="text-slate-500 mt-1 max-w-xl text-[12.5px]">
              Drillhole databases · 3D scenes · block-model estimation · CPR reports.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:shrink-0">
            {user?.role === "superadmin" && (
              <Link to="/app/admin/analytics"
                        data-testid="projects-toolbar-fleet-analytics"
                        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-[11.5px] text-slate-600 hover:border-brand-blue hover:text-brand-blue transition-colors"
                        title="Fleet-wide lifecycle + rollback KPIs">
                Fleet analytics
              </Link>
            )}
            <SeedDemoButton />
            <CreateDialog onCreated={(p) => nav(`/app/projects/${p.id}`)} />
          </div>
        </div>

        {isLoading ? (
          <div className="text-slate-500 text-sm mono">Loading projects…</div>
        ) : projects.length === 0 ? (
          <EmptyState onCreated={(p) => nav(`/app/projects/${p.id}`)} />
        ) : (
          <ProjectsListWithBanner projects={projects} nav={nav} del={del}
                                       includeArchived={includeArchived}
                                       archivedTotal={archivedTotal}
                                       counts={counts}
                                       onToggleArchived={() => setIncludeArchived((v) => !v)} />
        )}
      </div>
    </div>
  );
}

// Iter86 · Feb 2026 · Wraps the CRS Health banner + card grid so we can
// share the filter state between them. Banner click toggles the filter,
// and when active it thins the card list to just the projects that need
// CRS attention (rose or country-only) — an explicit "Clear filter" pill
// appears above the grid.
function classifyCRS(p) {
  const c = p.country || (p.location || {})?.country;
  const e = p.epsg || (p.crs_config || {})?.epsg;
  const d = p.datum || p.vertical_datum;
  if (c && e && d)          return "emerald";
  if (c && (e || d))         return "amber";
  if (c)                      return "rose";
  return "none";
}

function ProjectsListWithBanner({ projects, nav, del, includeArchived, archivedTotal = 0, counts, onToggleArchived }) {
  // Iter93 · Feb 2026 · capture the actor for the archive audit trail.
  const { user } = useAuth();
  const userEmail = user?.email || null;
  const [crsFilter, setCrsFilter] = useState(null);  // null | 'needs_crs'
  const [bulkOpen, setBulkOpen]   = useState(false);
  // Iter88 · Feb 2026 · Multi-select mode for batch delete / archive.
  const [selectMode, setSelectMode]   = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchBusy, setBatchBusy]     = useState(false);
  const [pendingDeletes, setPendingDeletes] = useState(new Set());
  const [reasonFilter, setReasonFilter] = useState(null); // Iter92 · Feb 2026
  // Iter92 · Feb 2026 · reset the reason filter when the archive view is
  // toggled off so it doesn't ambush the user next time they enable it.
  useEffect(() => {
    if (!includeArchived && reasonFilter) setReasonFilter(null);
  }, [includeArchived, reasonFilter]);
  const qc = useQueryClient();
  const { confirm, Portal: ConfirmPortal } = useConfirmDialog();
  const toggleSelect = (id) => {
    setSelectedIds((cur) => {
      const nxt = new Set(cur);
      if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
      return nxt;
    });
  };
  const clearSelect = () => { setSelectedIds(new Set()); setSelectMode(false); };
  const filtered = (crsFilter === "needs_crs"
    ? projects.filter((p) => ["rose", "none", "amber"].includes(classifyCRS(p)))
    : projects
  )
    .filter((p) => !pendingDeletes.has(p.id))       // Iter90 · hide 5 s soft-deletes
    // Iter92 · Feb 2026 · when a reason filter is on, keep archived
    // projects matching the reason. Non-archived items are hidden
    // because reasonFilter implies "focus on the archive shelf".
    .filter((p) => !reasonFilter
        || (p.archived && (p.archive_reason || "") === reasonFilter));
  const archivedCount = archivedTotal || projects.filter((p) => p.archived).length;
  const eligibleForBulk = projects.filter(
    (p) => !p.archived
        && ["rose", "amber"].includes(classifyCRS(p))
        && (p.country || (p.location || {})?.country)
  ).length;

  // Iter89 · Feb 2026 · Shared 4-worker pool used by both batch archive
  // and batch delete. Same pattern as BulkCRSFixDialog.
  const runInPool = async (ids, worker) => {
    const queue = [...ids];
    let ok = 0, fail = 0;
    const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) {
        const id = queue.shift();
        if (!id) break;
        try { await worker(id); ok++; }
        catch { fail++; }
      }
    });
    await Promise.all(workers);
    return { ok, fail };
  };

  // Iter90 · Feb 2026 · Undo-Toast helper. Wraps sonner's toast to
  // give the user a 5-second window to reverse an archive or delete.
  //
  // For archive → undo = PATCH { archived: false, archived_at: null,
  // archive_reason: null }, applied immediately.
  // For delete → we defer the actual DELETE by 5 seconds so undo can
  // simply cancel the setTimeout. The items are removed from the
  // visible list via `pendingDeletes` state, so the UX is the same
  // as a hard delete.
  const showUndoToast = ({ label, undoFn }) => {
    toast(label, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { try { undoFn?.(); } catch { /* no-op */ } },
      },
      "data-testid": "undo-toast",
    });
  };

  const scheduleHardDelete = (id, name) => {
    // Add to pending set so it disappears from the grid.
    setPendingDeletes((s) => new Set(s).add(id));
    const t = setTimeout(async () => {
      try { await api.delete(`/projects/${id}`); }
      catch (e) { toast.error(`Delete failed for "${name}": ${e?.message || "network"}`); }
      setPendingDeletes((s) => { const n = new Set(s); n.delete(id); return n; });
      qc.invalidateQueries({ queryKey: ["projects"] });
    }, 5000);
    showUndoToast({
      label: `"${name}" deleted`,
      undoFn: () => {
        clearTimeout(t);
        setPendingDeletes((s) => { const n = new Set(s); n.delete(id); return n; });
        toast.success(`"${name}" restored`);
      },
    });
  };

  const scheduleHardDeleteMany = (idNamePairs) => {
    const ids = idNamePairs.map(([id]) => id);
    setPendingDeletes((s) => { const n = new Set(s); ids.forEach((i) => n.add(i)); return n; });
    const timers = idNamePairs.map(([id, name]) => setTimeout(async () => {
      try { await api.delete(`/projects/${id}`); }
      catch (e) { toast.error(`Delete failed for "${name}": ${e?.message || "network"}`); }
      setPendingDeletes((s) => { const n = new Set(s); n.delete(id); return n; });
      qc.invalidateQueries({ queryKey: ["projects"] });
    }, 5000));
    showUndoToast({
      label: `Deleted ${idNamePairs.length} project${idNamePairs.length === 1 ? "" : "s"}`,
      undoFn: () => {
        timers.forEach(clearTimeout);
        setPendingDeletes((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; });
        toast.success(`Restored ${idNamePairs.length} project${idNamePairs.length === 1 ? "" : "s"}`);
      },
    });
  };

  const REASON_OPTIONS = [
    { value: "",          label: "— No reason —" },
    { value: "won",       label: "🏆 Won · asset acquired" },
    { value: "lost",      label: "😔 Lost · deal did not close" },
    { value: "paused",    label: "⏸ Paused · on hold" },
    { value: "completed", label: "✅ Completed · lifecycle done" },
    { value: "other",     label: "❓ Other" },
  ];

  const batchArchive = async () => {
    if (!selectedIds.size) return;
    // Iter93 · Feb 2026 · remember the user's last-used archive reason
    // so power-users only pick it once. Falls back to "" (no reason).
    const lastReason = (() => {
      try { return localStorage.getItem("sres.last-archive-reason") || ""; }
      catch { return ""; }
    })();
    const result = await confirm({
      title: `Archive ${selectedIds.size} project${selectedIds.size === 1 ? "" : "s"}?`,
      body:  "They will be hidden from the Projects grid. Optionally tag a reason so the archive shelf reads as a lifecycle graveyard.",
      confirmText: "Archive",
      testid: "confirm-archive",
      select: {
        name: "reason",
        label: "Reason (optional · last used pre-selected)",
        defaultValue: lastReason,
        options: REASON_OPTIONS,
      },
    });
    if (!result) return;
    const reason = result.values?.reason || null;
    // Iter93 · persist for next time
    try { localStorage.setItem("sres.last-archive-reason", reason || ""); } catch { /* silent */ }
    setBatchBusy(true);
    const ids = [...selectedIds];
    const { ok, fail } = await runInPool(ids, (id) =>
      api.patch(`/projects/${id}`, {
        archived: true,
        archived_at: new Date().toISOString(),
        archive_reason: reason,
        // Iter93 · audit trail — capture the actor. Server accepts the
        // value verbatim on the ProjectPatch model.
        archived_by: userEmail || null,
      })
    );
    setBatchBusy(false);
    if (ok) qc.invalidateQueries({ queryKey: ["projects"] });
    if (ok && !fail) {
      showUndoToast({
        label: `Archived ${ok} project${ok === 1 ? "" : "s"}${reason ? " · " + reason : ""}`,
        undoFn: async () => {
          await runInPool(ids, (id) =>
            api.patch(`/projects/${id}`, { archived: false, archived_at: null, archive_reason: null })
          );
          qc.invalidateQueries({ queryKey: ["projects"] });
          toast.success(`Restored ${ok} project${ok === 1 ? "" : "s"}`);
        },
      });
    } else if (ok && fail) toast.warning(`${ok} archived, ${fail} failed`);
    else toast.error(`Archive failed for all ${fail} projects`);
    clearSelect();
  };

  const batchDelete = async () => {
    if (!selectedIds.size) return;
    const yes = await confirm({
      title: `Delete ${selectedIds.size} project${selectedIds.size === 1 ? "" : "s"}?`,
      body:  "This will permanently remove all drillhole data, block models, and reports. You have 5 seconds to undo after confirming.",
      confirmText: "Delete",
      danger: true,
      testid: "confirm-delete-batch",
    });
    if (!yes) return;
    // Iter90 · defer the actual DELETE for 5 s so the Undo toast can
    // cancel it. Items are hidden from the grid immediately.
    const pairs = projects
      .filter((p) => selectedIds.has(p.id))
      .map((p) => [p.id, p.name]);
    scheduleHardDeleteMany(pairs);
    clearSelect();
  };

  // Iter89 · Feb 2026 · Single-card delete uses the same themed dialog.
  const askDeleteOne = async (project) => {
    const yes = await confirm({
      title: `Delete "${project.name}"?`,
      body:  "This will permanently remove all drillhole data, block models, and reports for this project. You have 5 seconds to undo after confirming.",
      confirmText: "Delete",
      danger: true,
      testid: "confirm-delete-one",
    });
    if (yes) scheduleHardDelete(project.id, project.name);
  };

  // Iter89 · Feb 2026 · Single-card restore.
  // Iter94 · Feb 2026 · pops the RestoreWizardDialog so the user can
  // add a fresh reactivation note that enriches the audit trail.
  const [restoreTarget, setRestoreTarget] = useState(null);
  const restoreOne = (project) => setRestoreTarget(project);
  const applyRestore = async (project, note) => {
    try {
      await api.patch(`/projects/${project.id}`, {
        archived: false, archived_at: null, archive_reason: null,
        archived_by: userEmail || null,
        archive_note: note || null,
      });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["archive-events", project.id] });
      qc.invalidateQueries({ queryKey: ["projects-analytics"] });
      toast.success(`Restored "${project.name}"`);
    } catch (e) {
      toast.error(`Could not restore: ${e?.message || "network"}`);
    }
  };

  // Iter90 · Feb 2026 · Batch Restore — mirrors batch archive. Only
  // shown in the action bar when every selected project is archived.
  // Iter92 · Feb 2026 · snapshot the reason on each project BEFORE
  // clearing so Undo can restore the exact archive state per project.
  const batchRestore = async () => {
    if (!selectedIds.size) return;
    setBatchBusy(true);
    const ids = [...selectedIds];
    // Snapshot for undo: each entry = {id, archived_at, archive_reason}.
    const snapshot = projects
      .filter((p) => selectedIds.has(p.id) && p.archived)
      .map((p) => ({ id: p.id, archived_at: p.archived_at || null, archive_reason: p.archive_reason || null }));
    const { ok, fail } = await runInPool(ids, (id) =>
      api.patch(`/projects/${id}`, { archived: false, archived_at: null, archive_reason: null })
    );
    setBatchBusy(false);
    if (ok) qc.invalidateQueries({ queryKey: ["projects"] });
    if (ok && !fail) {
      showUndoToast({
        label: `Restored ${ok} project${ok === 1 ? "" : "s"}`,
        undoFn: async () => {
          // Re-apply the snapshot: archive each project back with its
          // original reason + timestamp.
          await runInPool(snapshot, (row) =>
            api.patch(`/projects/${row.id}`, {
              archived: true,
              archived_at: row.archived_at || new Date().toISOString(),
              archive_reason: row.archive_reason,
            })
          );
          qc.invalidateQueries({ queryKey: ["projects"] });
          toast.success(`Re-archived ${snapshot.length} project${snapshot.length === 1 ? "" : "s"}`);
        },
      });
    } else if (ok && fail) toast.warning(`${ok} restored, ${fail} failed`);
    else toast.error(`Restore failed for all ${fail} projects`);
    clearSelect();
  };

  // How many of the currently-selected projects are archived? Drives
  // the action-bar layout — pure archive → show only Restore; mixed
  // or none-archived → show Archive + Delete.
  const selectedArchivedCount = projects
    .filter((p) => selectedIds.has(p.id) && p.archived).length;
  const allSelectedArchived = selectedIds.size > 0
    && selectedArchivedCount === selectedIds.size;

  return (
    <>
      {eligibleForBulk > 0 && (
        <div className="mb-6 flex items-start">
          {/* Iter87 · Feb 2026 · Bulk Re-Project CRS entry point.
              Kept as a stand-alone card after the Portfolio CRS Health
              banner was removed (user request · Round 8). */}
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            data-testid="portfolio-bulk-crs-btn"
            className="inline-flex flex-col items-center justify-center gap-1
                        rounded-lg border-2 border-amber-300 bg-gradient-to-b from-amber-50 to-white
                        px-4 py-3 text-amber-900 hover:border-amber-400 hover:shadow-md
                        transition-all min-w-[140px]"
            title={`Batch re-project ${eligibleForBulk} project(s) needing CRS`}
          >
            <span className="text-[10px] uppercase tracking-widest font-mono opacity-70">Bulk Fix</span>
            <span className="text-[13px] font-heading font-black">Re-Project CRS</span>
            <span className="text-[10.5px] font-mono">{eligibleForBulk} project{eligibleForBulk === 1 ? "" : "s"}</span>
          </button>
        </div>
      )}
      {/* Iter88 · Feb 2026 · Select toggle for batch delete/archive.
          Iter89 · Feb 2026 · Also hosts the View-Archived filter chip. */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => { if (selectMode) clearSelect(); else setSelectMode(true); }}
          data-testid="portfolio-select-toggle"
          className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest
                       rounded-md border px-2.5 py-1 transition
                       ${selectMode
                         ? "border-brand-blue bg-brand-blue text-white"
                         : "border-slate-300 text-slate-600 hover:border-brand-blue hover:text-brand-blue"}`}
        >
          {selectMode ? "Cancel select" : "Select…"}
        </button>
        {selectMode && (
          <span className="text-[11px] text-slate-500 font-mono"
                    data-testid="portfolio-selected-count">
            {selectedIds.size} selected
          </span>
        )}
        {/* Iter89 · View Archived filter chip */}
        <button
          type="button"
          onClick={onToggleArchived}
          data-testid="portfolio-view-archived-toggle"
          className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest
                       rounded-md border px-2.5 py-1 transition
                       ${includeArchived
                         ? "border-slate-800 bg-slate-800 text-white"
                         : "border-slate-300 text-slate-600 hover:border-slate-800 hover:text-slate-800"}`}
          title={includeArchived ? "Hide archived projects" : "Show archived projects too"}
        >
          {includeArchived ? "Hide archived" : `View archived${archivedCount ? " · " + archivedCount : ""}`}
        </button>
        {/* Iter92 · Feb 2026 · Reason filter chips. Only visible when
            the user is looking at the archive shelf. Clicking one filters
            the grid to only projects with that reason; clicking again
            or clicking `All` clears the filter.
            Iter93 · Feb 2026 · each chip shows a live count from
            /api/projects/counts.by_reason so users see the split
            without applying the filter. */}
        {includeArchived && (
          <div className="inline-flex items-center gap-1 pl-2 border-l border-slate-200 ml-1"
                    data-testid="portfolio-reason-filter-bar">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono pr-1">Reason</span>
            {[
              { v: null,        l: "All",     bg: "bg-slate-100 text-slate-700",   active: "bg-slate-800 text-white",   n: archivedCount },
              { v: "won",       l: "🏆 Won",  bg: "bg-emerald-50 text-emerald-700", active: "bg-emerald-600 text-white", n: counts?.by_reason?.won      || 0 },
              { v: "lost",      l: "😔 Lost", bg: "bg-rose-50 text-rose-700",       active: "bg-rose-600 text-white",    n: counts?.by_reason?.lost     || 0 },
              { v: "paused",    l: "⏸ Paused", bg: "bg-amber-50 text-amber-700",     active: "bg-amber-600 text-white",   n: counts?.by_reason?.paused   || 0 },
              { v: "completed", l: "✅ Done",  bg: "bg-slate-100 text-slate-600",    active: "bg-slate-600 text-white",   n: counts?.by_reason?.completed|| 0 },
            ].map((c) => {
              const isActive = reasonFilter === c.v;
              return (
                <button
                  key={String(c.v)}
                  type="button"
                  onClick={() => setReasonFilter(c.v)}
                  data-testid={`portfolio-reason-filter-${c.v || "all"}`}
                  data-active={isActive ? "true" : "false"}
                  data-count={c.n}
                  className={`inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-widest
                                rounded-md px-2 py-0.5 transition ${isActive ? c.active : c.bg}`}
                >
                  <span>{c.l}</span>
                  <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full
                                    text-[9px] font-bold ${isActive ? "bg-white/20" : "bg-white/70 text-slate-700"}`}
                            data-testid={`portfolio-reason-count-${c.v || "all"}`}>
                    {c.n}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {crsFilter === "needs_crs" && (
        <div className="mb-4 flex items-center gap-2 text-[11px] font-mono"
                  data-testid="projects-filter-active">
          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 uppercase tracking-widest ring-1 ring-amber-300">
            Filter: projects that need CRS ({filtered.length}/{projects.length})
          </span>
          <button
            onClick={() => setCrsFilter(null)}
            data-testid="projects-filter-clear"
            className="text-slate-500 hover:text-brand-blue underline underline-offset-2">
            Clear filter
          </button>
        </div>
      )}
      {/* Iter93 · Feb 2026 · Lifecycle funnel shown when the archive
          shelf is on so leadership can see win-rate + pipeline health
          at a glance. */}
      {includeArchived && (
        <div className="mb-4">
          <LifecycleFunnel byReason={counts?.by_reason || {}} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-list">
        {filtered.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            selectMode={selectMode}
            selected={selectedIds.has(p.id)}
            onToggleSelect={() => toggleSelect(p.id)}
            onOpen={() => selectMode ? toggleSelect(p.id) : nav(`/app/projects/${p.id}`)}
            onResume={(stepId) => nav(`/app/projects/${p.id}/wizard/${stepId}?resume=1`)}
            onDelete={() => askDeleteOne(p)}
            onRestore={() => restoreOne(p)}
          />
        ))}
      </div>
      {/* Iter88 · Feb 2026 · Sticky batch action bar. Slides up from the
          bottom when at least one project is selected.
          Iter90 · Feb 2026 · shows Restore instead of Archive/Delete
          when every selected project is archived. */}
      {selectMode && selectedIds.size > 0 && (
        <div
          data-testid="portfolio-batch-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3
                       rounded-full bg-slate-900 text-white px-4 py-2 shadow-2xl border border-slate-700"
        >
          <span className="text-[11.5px] font-mono">
            {selectedIds.size} project{selectedIds.size === 1 ? "" : "s"} selected
            {selectedArchivedCount > 0 && selectedArchivedCount < selectedIds.size && (
              <span className="text-slate-400"> · {selectedArchivedCount} archived</span>
            )}
          </span>
          {allSelectedArchived ? (
            <Button
              size="sm"
              onClick={batchRestore} disabled={batchBusy}
              data-testid="portfolio-batch-restore"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]"
            >
              {batchBusy ? "…" : "Restore"}
            </Button>
          ) : (
            <>
              <Button
                size="sm" variant="outline"
                onClick={batchArchive} disabled={batchBusy}
                data-testid="portfolio-batch-archive"
                className="bg-transparent border-slate-500 text-white hover:bg-slate-800 h-7 text-[11px]"
              >
                {batchBusy ? "…" : "Archive"}
              </Button>
              <Button
                size="sm"
                onClick={batchDelete} disabled={batchBusy}
                data-testid="portfolio-batch-delete"
                className="bg-rose-600 hover:bg-rose-700 text-white h-7 text-[11px]"
              >
                {batchBusy ? "…" : "Delete"}
              </Button>
            </>
          )}
          <button
            onClick={clearSelect}
            data-testid="portfolio-batch-clear"
            className="text-slate-300 hover:text-white text-[11px] pl-2"
          >
            Cancel
          </button>
        </div>
      )}
      <BulkCRSFixDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        projects={projects}
      />
      {/* Iter94 · Feb 2026 · Reopen Wizard — captures a fresh note on
          single-card restore so the audit trail records the why. */}
      <RestoreWizardDialog
        open={!!restoreTarget}
        onOpenChange={(o) => { if (!o) setRestoreTarget(null); }}
        project={restoreTarget}
        onConfirm={(note) => { if (restoreTarget) applyRestore(restoreTarget, note); setRestoreTarget(null); }}
      />
      <ConfirmPortal />
    </>
  );
}

// Iter85 · Feb 2026 · Portfolio-wide CRS health hero banner. Reads the
// same client-side classifier the per-card chip uses so the two stay in
// lockstep with no extra backend work.
function CRSHealthBanner({ projects, crsFilter, onToggleFilter }) {
  const buckets = { emerald: 0, amber: 0, rose: 0, none: 0 };
  for (const p of projects) {
    buckets[classifyCRS(p)] += 1;
  }
  const total = projects.length;
  const needsCrs = buckets.rose + buckets.none;
  const partial  = buckets.amber;
  const healthy  = buckets.emerald;
  const percentHealthy = total ? Math.round((healthy / total) * 100) : 0;
  const tone = needsCrs > 0 ? "rose" : partial > 0 ? "amber" : "emerald";
  const toneCls = {
    emerald: "border-emerald-300 from-emerald-50 to-white text-emerald-900",
    amber:   "border-amber-300 from-amber-50 to-white text-amber-900",
    rose:    "border-rose-300 from-rose-50 to-white text-rose-900",
  }[tone];
  const dotCls = { emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500" }[tone];
  const headline = needsCrs > 0
    ? `${needsCrs} of ${total} projects need CRS`
    : partial > 0
    ? `${partial} of ${total} projects have partial CRS`
    : `All ${total} projects are CRS-healthy`;
  // Iter86 · Feb 2026 · daily healthy% snapshot for the sparkline.
  // Stored in localStorage so it's zero-backend + user-specific.
  useDailyHealthSnapshot(percentHealthy, total);
  const trend = useHealthTrend(30);
  const isFiltered = crsFilter === "needs_crs";
  const canFilter  = needsCrs > 0 || partial > 0;
  const bannerCommon = `mb-6 rounded-lg border-2 bg-gradient-to-br ${toneCls} p-4 flex items-center gap-4 w-full text-left`;
  const bannerCls = canFilter
    ? `${bannerCommon} cursor-pointer transition hover:shadow-md hover:-translate-y-0.5
         ${isFiltered ? "ring-2 ring-brand-blue/40" : ""}`
    : bannerCommon;
  const Cmp = canFilter ? "button" : "div";
  return (
    <Cmp className={bannerCls}
            {...(canFilter ? { type: "button", onClick: onToggleFilter } : {})}
            data-testid="portfolio-crs-health-banner"
            data-tone={tone}
            data-filter-active={isFiltered ? "true" : "false"}
            title={canFilter ? "Click to filter to projects needing CRS attention" : "CRS Health Snapshot"}>
      <span className={`shrink-0 w-3 h-3 rounded-full ${dotCls} ring-4 ring-white/60 animate-pulse`}></span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-mono opacity-70 flex items-center gap-1.5">
          Portfolio CRS Health · one-glance snapshot
          {canFilter && (
            <span data-testid="portfolio-crs-filter-hint"
                     className="normal-case tracking-normal text-slate-500 italic">
              · {isFiltered ? "click to clear filter" : "click to filter"}
            </span>
          )}
        </div>
        <div className="text-[16px] font-heading font-black tracking-tight leading-tight"
                data-testid="portfolio-crs-headline">
          {headline}
        </div>
        <div className="text-[11.5px] mt-0.5 font-mono">
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>{healthy} healthy
          </span>
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{partial} partial
          </span>
          <span className="inline-flex items-center gap-1 mr-3">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>{buckets.rose} country-only
          </span>
          {buckets.none > 0 && (
            <span className="inline-flex items-center gap-1 text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>{buckets.none} unset
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-4">
        <HealthSparkline series={trend} tone={tone} />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-70">Healthy</div>
          <div className="text-[22px] font-heading font-black tabular-nums leading-none"
                  data-testid="portfolio-crs-healthy-pct">
            {percentHealthy}%
          </div>
        </div>
      </div>
    </Cmp>
  );
}

// ── 30-day healthy% sparkline (client-side only) ─────────────────────
function useDailyHealthSnapshot(percentHealthy, total) {
  useEffect(() => {
    if (!total) return;
    try {
      const key = "sres.crs-health-trend";
      const today = new Date().toISOString().slice(0, 10);
      const raw = JSON.parse(localStorage.getItem(key) || "[]");
      const rest = raw.filter((r) => r.d !== today).slice(-29);
      rest.push({ d: today, v: percentHealthy });
      localStorage.setItem(key, JSON.stringify(rest));
    } catch { /* silent */ }
  }, [percentHealthy, total]);
}

function useHealthTrend(days = 30) {
  return useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sres.crs-health-trend") || "[]");
      return raw.slice(-days);
    } catch { return []; }
  }, [days]);
}

function HealthSparkline({ series, tone }) {
  if (!series || series.length < 2) {
    return (
      <div className="hidden md:flex items-center gap-1.5 text-[9.5px] font-mono opacity-60"
                data-testid="portfolio-crs-sparkline-empty">
        <span>—</span>
        <span>30-day trend<br/>builds after 2 days</span>
      </div>
    );
  }
  const W = 96;
  const H = 32;
  const min = 0;
  const max = 100;
  const stroke = { emerald: "#10b981", amber: "#f59e0b", rose: "#f43f5e" }[tone] || "#64748b";
  const pts = series.map((r, i) => {
    const x = (i / (series.length - 1)) * (W - 2) + 1;
    const y = H - 1 - ((r.v - min) / (max - min)) * (H - 2);
    return [x, y];
  });
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const last = series[series.length - 1].v;
  const first = series[0].v;
  const delta = last - first;
  return (
    <div className="hidden md:flex flex-col items-end" data-testid="portfolio-crs-sparkline">
      <svg width={W} height={H} className="block"
             aria-label={`30-day CRS health trend: ${series.length} snapshots, current ${last}%`}>
        <path d={d} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        {pts.length > 0 && (
          <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill={stroke} />
        )}
      </svg>
      <div className="text-[9px] uppercase tracking-widest font-mono opacity-70 mt-0.5">
        30-day · {delta >= 0 ? "▲" : "▼"}{Math.abs(delta)}%
      </div>
    </div>
  );
}

function EmptyState({ onCreated }) {
  return (
    <div className="surface p-10 flex flex-col items-center text-center animate-fade-in" data-testid="empty-projects">
      <Database size={48} weight="thin" className="text-brand-blue mb-4" />
      <div className="font-heading text-xl mb-2">No projects yet</div>
      <p className="text-slate-500 text-sm mb-6 max-w-md">
        Create a project to upload drillhole CSVs and start building block models.
        Standard columns are supported: Collar (hole_id,x,y,z,depth), Survey, Assay, Geology.
      </p>
      <CreateDialog onCreated={onCreated} />
    </div>
  );
}

// -------------------------------------------------------------------------
// ProjectCard · single project tile with a "Resume where I left off" button
// that deep-links to the first incomplete wizard step. Falls back gracefully
// when progress data can't be fetched (network / brand-new project).
// -------------------------------------------------------------------------
// Feb 2026 · Iter87 · Legacy step id → 9-step stage lookup lets us
// show the sidebar-consistent stage number ("Stage 6 · Estimation
// Settings") on the Resume button. Falls back to Stage 2 when the
// wizard-progress endpoint hasn't returned yet (brand-new projects).
function _stageForStepId(stepId) {
  const s = resolveStageForWizardStep(stepId);
  if (s) return s;
  // Common legacy step ids not in WORKFLOW_STAGES yet
  if (stepId === "project_setup") return WORKFLOW_STAGES.find((x) => x.key === "project_details");
  if (stepId === "cpr")           return WORKFLOW_STAGES.find((x) => x.key === "generate_report");
  if (stepId === "qaqc")          return WORKFLOW_STAGES.find((x) => x.key === "validate_autofix");
  return WORKFLOW_STAGES.find((x) => x.key === "project_details");
}
const STEP_TITLES = {
  project_setup:   "Project Setup",
  data_upload:     "Data Upload",
  data_validation: "Data Validation",
  qaqc:            "QA/QC",
  statistics:      "Statistics",
  composite:       "Composite",
  domain:          "Domain",
  variogram:       "Variogram",
  block_model:     "Block Model",
  classification:  "Classification",
  cpr:             "CPR Generation",
};
const ORDERED_STEPS = Object.keys(STEP_TITLES);

function ProjectCard({ project: p, onOpen, onResume, onDelete, onRestore,
                        selectMode = false, selected = false, onToggleSelect }) {
  const { data: progress } = useQuery({
    queryKey: ["wizard-step-progress", p.id],
    queryFn: () => getWizardStepProgress(p.id),
    staleTime: 30_000,
    retry: false,
  });

  // Iter83 · Feb 2026 · Portfolio CRS Health chip. We piggy-back on the
  // existing /gis/crs/country-defaults endpoint (no new backend). If
  // the project has a country + a CRS/EPSG set, health = ok. If it
  // has a country but no EPSG, health = warn. If neither, health =
  // critical. Chip is silent (no chip) for very-new projects.
  const health = (() => {
    const country = p.country || (p.location || {})?.country;
    const epsg = p.epsg || (p.crs_config || {})?.epsg;
    const datum = p.datum || p.vertical_datum;
    if (!country && !epsg) return null;
    if (country && epsg && datum) return { tone: "emerald", label: "CRS OK", detail: `EPSG:${epsg}` };
    if (country && (epsg || datum)) return { tone: "amber", label: "CRS Partial", detail: "Missing datum or EPSG" };
    return { tone: "rose", label: "CRS Missing", detail: "Country only — no EPSG" };
  })();

  // Prefer the deepest step the user has touched (only if it's incomplete).
  // Otherwise fall back to the first incomplete step from server.
  const resumeStep = (() => {
    if (!progress) return "project_setup";
    const { deepest_step, next_step, completed } = progress;
    if (deepest_step && !completed?.[deepest_step]) return deepest_step;
    return next_step || "project_setup";
  })();
  // Feb 2026 · Iter87 · resolve the internal step id back to the
  // 9-step stage so the Resume button and the sidebar stay in sync.
  const stage       = _stageForStepId(resumeStep);
  const stageNumber = stage?.n || 2;
  const stageLabel  = stage?.label || "Project Details";
  const completedCount = progress
    ? Object.values(progress.completed || {}).filter(Boolean).length
    : 0;
  const pctComplete = Math.round((completedCount / ORDERED_STEPS.length) * 100);
  const hasProgress = completedCount > 0;

  return (
    <div
      onClick={onOpen}
      className={`surface p-5 cursor-pointer surface-hover transition-colors group relative
                    ${selectMode && selected ? "ring-2 ring-brand-blue" : ""}
                    ${p.archived ? "opacity-60 grayscale-[0.4] hover:opacity-90" : ""}`}
      data-testid={`project-card-${p.id}`}
      data-archived={p.archived ? "true" : "false"}
    >
      {/* Iter89 · Feb 2026 · Archived badge — visible in archived-view.
          Iter90 · Feb 2026 · surfaces the archive_reason so the shelf
          reads as a lifecycle graveyard.
          Iter92 · Feb 2026 · pill is colour-coded by reason:
            won → emerald, lost → rose, paused → amber, completed →
            slate, other/unset → slate. */}
      {p.archived && (() => {
        const REASON_COLOURS = {
          won:       "bg-emerald-600",
          lost:      "bg-rose-600",
          paused:    "bg-amber-600 text-amber-50",
          completed: "bg-slate-600",
          other:     "bg-slate-700",
        };
        const bg = REASON_COLOURS[p.archive_reason] || "bg-slate-800";
        // Iter93 · Feb 2026 · richer tooltip surfaces the archive audit
        // (who + when + reason) without cluttering the card.
        const tip = [
          p.archived_at ? `Archived ${new Date(p.archived_at).toLocaleString()}` : null,
          p.archived_by ? `by ${p.archived_by}` : null,
          p.archive_reason ? `· ${p.archive_reason}` : null,
        ].filter(Boolean).join(" ");
        return (
          <div className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1
                                rounded-full text-white text-[9px] uppercase tracking-widest
                                font-mono px-2 py-0.5 ${bg}`}
                    data-testid={`project-archived-badge-${p.id}`}
                    data-reason={p.archive_reason || "none"}
                    data-archived-by={p.archived_by || ""}
                    title={tip}>
            Archived{p.archive_reason ? ` · ${p.archive_reason}` : ""}
          </div>
        );
      })()}
      {/* Iter88 · Feb 2026 · selection checkbox appears only in select mode. */}
      {selectMode && (
        <div
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
          className="absolute top-3 left-3 z-10"
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`project-select-${p.id}`}
            className="w-4 h-4 rounded border-slate-300 accent-brand-blue"
          />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-brand-blue">{p.commodity}</div>
          <div className="font-heading font-bold text-lg mt-1 leading-tight">{p.name}</div>
          <div className="text-xs text-slate-500 mono mt-1">
            {new Date(p.created_at).toLocaleDateString()}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {p.archived && onRestore && (
            <button
              onClick={(e) => { e.stopPropagation(); onRestore(); }}
              className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-emerald-600
                              transition-opacity text-[10px] uppercase tracking-widest font-mono
                              border border-slate-300 hover:border-emerald-500 rounded px-1.5 py-0.5"
              data-testid={`restore-project-${p.id}`}
              title="Restore this project"
            >
              Restore
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
            data-testid={`delete-project-${p.id}`}
          >
            <Trash size={18} />
          </button>
        </div>
      </div>
      {p.description && (
        <p className="text-xs text-slate-500 mt-3 line-clamp-2">{p.description}</p>
      )}
      <div className="mt-4 flex gap-1.5 flex-wrap">
        {[
          ["collar", p.has_collar],
          ["survey", p.has_survey],
          ["assay", p.has_assay],
          ["geology", p.has_geology],
        ].map(([k, ok]) => (
          <span
            key={k}
            className={`text-[10px] tabular uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
              ok
                ? "border-brand-blue/40 text-brand-blue bg-brand-blue/10"
                : "border-slate-200 text-slate-500"
            }`}
          >
            {k}
          </span>
        ))}
        {health && (
          (health.tone === "emerald") ? (
            <span
              data-testid={`project-card-crs-health-${p.id}`}
              data-tone={health.tone}
              title={`${health.label}${health.detail ? " · " + health.detail : ""}`}
              className="text-[10px] tabular uppercase tracking-widest px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1
                             border-emerald-300 text-emerald-700 bg-emerald-50">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {health.label}
            </span>
          ) : (
            <Link
              to={`/app/projects/${p.id}/setup?focus=country`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`project-card-crs-health-${p.id}`}
              data-tone={health.tone}
              title={`${health.label}${health.detail ? " · " + health.detail : ""} · Click to fix in Project Details`}
              className={`group text-[10px] tabular uppercase tracking-widest px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 transition
                ${health.tone === "amber"
                  ? "border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-500"
                  : "border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 hover:border-rose-500"}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                health.tone === "amber" ? "bg-amber-500" : "bg-rose-500"
              }`}></span>
              <span className="group-hover:hidden">{health.label}</span>
              <span className="hidden group-hover:inline">Fix in details →</span>
            </Link>
          )
        )}
      </div>

      {/* Progress + Resume CTA */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        {hasProgress ? (
          <div className="mb-2">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mono uppercase tracking-widest">
              <span>Wizard progress</span>
              <span data-testid={`project-progress-pct-${p.id}`}>{pctComplete}%</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div
                className="h-full bg-brand-blue rounded-full transition-all"
                style={{ width: `${pctComplete}%` }}
              />
            </div>
          </div>
        ) : null}
        <Button
          onClick={(e) => { e.stopPropagation(); onResume(resumeStep); }}
          size="sm"
          className="w-full bg-brand-blue hover:bg-brand-blueDeep text-white rounded-sm font-medium h-9"
          data-testid={`project-resume-${p.id}`}
        >
          <Play size={12} weight="fill" className="mr-2" />
          {hasProgress ? "Resume · Stage" : "Start · Stage"} {stageNumber} · {stageLabel}
          <ArrowRight size={12} className="ml-auto" />
        </Button>
      </div>
    </div>
  );
}



function SeedDemoButton() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [pending, setPending] = useState(false);

  const { data } = useQuery({
    queryKey: ["seed-catalogue"],
    queryFn: listSeedCatalogue,
    enabled: user?.role === "superadmin",
  });
  const datasets = data?.datasets || [];

  if (user?.role !== "superadmin") return null;

  const trigger = async (slug, label) => {
    if (!confirm(`Seed the ${label} demo project? This will replace any previous demo with the same name.`)) return;
    setPending(true);
    try {
      const res = await seedDemoProject(slug);
      toast.success(`${res.project_name} · ${res.files.reduce((n, f) => n + f.rows, 0)} rows loaded`);
      qc.invalidateQueries({ queryKey: ["projects"] });
      nav(`/app/projects/${res.project_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not seed demo project");
    } finally {
      setPending(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={pending}
          className="border-brand-blue/40 text-brand-blue hover:bg-brand-blue hover:text-white font-medium"
          data-testid="seed-demo-btn"
        >
          <Sparkle size={14} weight="fill" className="mr-2" />
          {pending ? "Seeding…" : "Load demo project"}
          <CaretDown size={12} className="ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[280px] bg-white border-slate-200">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
          Bundled datasets
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        {datasets.map((d) => (
          <DropdownMenuItem
            key={d.slug}
            onSelect={() => trigger(d.slug, d.name)}
            className="cursor-pointer flex-col items-start py-3"
            data-testid={`seed-demo-${d.slug}`}
          >
            <div className="flex items-center gap-2 w-full">
              <span className="text-[11px] font-mono text-brand-blue">{d.commodity}</span>
              <span className="font-semibold text-slate-900">{d.name}</span>
            </div>
            <div className="text-[12px] text-slate-500 mt-1 line-clamp-2">
              {d.description}
            </div>
          </DropdownMenuItem>
        ))}
        {!datasets.length ? (
          <DropdownMenuItem disabled className="text-slate-500 text-sm">
            No bundled datasets available
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
